<script setup lang="ts">
import { equipCollection } from '@/core/equip'
import { useUserStore } from '@/stores/modules/user'
import type { EquipItemType } from '@/types/equip'
import { computed, ref } from 'vue'
import { equipIcon, equipQualityText, equipTypeList, noPercentEquipAttrKeyList, supremeAffixLevelQualityMap } from '../../item/data'
import EquipInfo from '../../item/components/EquipInfo.vue'
import { getEngravePrice } from '../data'
import { socketEmitAsync } from '@/utils/web-socket'
import type { SupremeEngraveRes } from '@/types/smithy'
import { formatNumberWithCommas } from '@/utils'
import { ElMessage } from 'element-plus'
import { getEquipName } from '@/utils/equip'
import { QuestionFilled } from '@element-plus/icons-vue'
import { cloneDeep } from 'lodash'
import type { UserAttrType } from '@/types/user'
import { supremeAffixCollection, engraveSupremeMaxQulityMap } from '@/core/equip/supreme'
import { getSkillNameById } from '@/core/skill'
import { BasicAttrTextEnum } from '@/enums'

const userStore = useUserStore()

const equipItemId = ref('')
const equipInfo = computed(() => {
  const res = userStore.itemList.find((item) => item.id === equipItemId.value)
  if (!res) {
    equipItemId.value = ''
    return undefined
  }
  const { name, type, attrs } = equipCollection[res.equipId]
  return { ...res, name, type, attrs }
})
const equippedList = computed(() =>
  equipTypeList.value.map((item) => {
    const itemId = userStore.equippedList[item]
    const equipInfo = userStore.itemList.find((item) => item.id === itemId)
    if (!equipInfo) return undefined
    const { name, type, attrs } = equipCollection[equipInfo.equipId]
    return { ...equipInfo, name, type, attrs }
  }),
)

const itemList = computed<EquipItemType[]>(() => {
  const items = userStore.itemList
  if (userStore.itemList.length === 0) return []
  return items
    .filter((item) => {
      const equippedIdList = Object.values(userStore.equippedList)
      if (!item.supremeAttrs) return false
      return !equippedIdList.includes(item.id)
    })
    .map((item) => {
      const { equipId } = item
      const { name, type, attrs } = equipCollection[equipId]
      return { ...item, name, type, attrs }
    })
})

const engraveMaxQuality = computed(() => {
  if (!equipInfo.value || !equipInfo.value.supremeAttrs) return ''
  return engraveSupremeMaxQulityMap[equipInfo.value.supremeAttrs.level]
})

const engraveIndex = ref(1)
function handleChoice(item: EquipItemType | undefined) {
  if (item && !item.supremeAttrs) return ElMessage.warning('仅至臻装备可刻印')
  equipItemId.value = item?.id || ''
  useCoupon.value = false
  engraveIndex.value = item?.supremeEngraveAttr?.affixIndex || 1
}

const useCoupon = ref(false)
function beforeUseCouponChange() {
  if (!userStore.consumables.supremeEngraveCoupon) {
    ElMessage.warning('您未拥有至臻刻印券')
    return false
  }
  return true
}

const resShow = ref(false)
const resEquipInfo = ref()
const isRecord = ref(false)
async function handleEngrave() {
  if (!equipInfo.value) return
  const { id, supremeAttrs, supremeEngraveAttr } = equipInfo.value
  const price = getEngravePrice(supremeEngraveAttr?.count, supremeAttrs?.level)

  const {
    engraveAttr: resEngraveAttr,
    userAttr,
    isRecord: resIsRecord,
  } = await socketEmitAsync<SupremeEngraveRes>({ type: 'supremeEngraveEquip', data: { id, index: engraveIndex.value, useCoupon: useCoupon.value } })
  resEquipInfo.value = undefined
  isRecord.value = resIsRecord
  if (userAttr) userStore.userAttr = userAttr
  if (!resIsRecord) {
    if (useCoupon.value) {
      userStore.consumables.supremeEngraveCoupon--
      if (userStore.consumables.supremeEngraveCoupon <= 0) useCoupon.value = false
    } else {
      userStore.gold -= price
    }
  }

  const item = userStore.itemList.find((item) => item.id === id)
  if (!item || !equipInfo.value) return
  if (resEngraveAttr?.count === 1) {
    item.supremeEngraveAttr = resEngraveAttr
    equipInfo.value.supremeEngraveAttr = resEngraveAttr
  } else {
    resEquipInfo.value = {
      ...cloneDeep(equipInfo.value),
      supremeEngraveAttr: resEngraveAttr,
    }
  }

  resShow.value = true
}
async function handleChoiceEngraveRes(type: 0 | 1) {
  ElMessageBox.confirm(
    `<p class="affix">确定<span class="info">${type === 0 ? '保留' : '覆盖'}</span>刻印吗？（该操作将<span class="shrink">无法撤销</span>）</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
      beforeClose: async (action, instance, done) => {
        if (action !== 'confirm') return done()
        if (!equipInfo.value || !resEquipInfo.value) return done()
        try {
          instance.confirmButtonLoading = true
          instance.confirmButtonText = '加载中...'

          const itemId = equipInfo.value.id

          const userAttr = await socketEmitAsync<UserAttrType>({ type: 'choiceSupremeEngraveRes', data: { itemId, choiceIndex: type } })
          const item = userStore.itemList.find((item) => item.id === itemId)
          if (!item) return done()
          if (userAttr) {
            userStore.userAttr = userAttr

            const resEngraveAttr = resEquipInfo.value.supremeEngraveAttr
            item.supremeEngraveAttr = resEngraveAttr
            equipInfo.value.supremeEngraveAttr = resEngraveAttr
          } else {
            if (!item.supremeEngraveAttr || !equipInfo.value.supremeEngraveAttr) return done()
            item.supremeEngraveAttr.count++
          }

          resShow.value = false
          ElMessage.success('刻印确认成功')
          done()
        } finally {
          instance.confirmButtonLoading = false
          instance.confirmButtonText = '确定'
        }
      },
    },
  )
}
</script>

<template>
  <div class="enchant">
    <div class="enchant-table border-wrap">
      <h4>锻造台</h4>
      <div class="enchant-equip">
        <CommonBtn style="width: 100%" @done="handleChoice(undefined)">
          <span v-if="equipInfo" v-html="getEquipName(equipInfo, equipInfo.type)"></span>
          <span v-else>(未放入)</span>
        </CommonBtn>
      </div>
      <div class="help-info">
        <el-popover placement="bottom" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>刻印说明</h4>
            <p>1. 通过<span class="info">刻印</span>，可指定装备的一条词缀进行<span class="info">重新随机</span>。</p>
            <p>2. 您仅可选择一个非首词缀进行重新随机，刻印后将<span class="info">锁定</span>刻印词缀。</p>
            <p>3. 刻印产出的词缀品质及概率根据装备本身的<span class="info">品级</span>决定。</p>
            <p>
              4. 首次刻印<span class="grow">免费</span>，而后刻印花费将随<span class="info">刻印次数</span>逐步增加，直至
              <span class="grow">10次</span>时达到最大值，同时刻印的花费受装备的<span class="info">品级</span>影响。
            </p>
            <p>5. 第二次刻印开始，可自主选择保留/覆盖刻印词缀，若您忘记选择，可再次对该装备刻印以继续选择。</p>
          </div>
        </el-popover>
      </div>
      <template v-if="equipInfo">
        <div class="info-card">
          <p style="display: flex; align-items: center">
            <span>刻印次数：{{ equipInfo.supremeEngraveAttr?.count || 0 }}</span>
            <span
              >使用刻印券（{{ userStore.consumables.supremeEngraveCoupon }}）：<el-switch
                v-model="useCoupon"
                size="small"
                :before-change="beforeUseCouponChange"
            /></span>
          </p>
          <p>
            刻印花费：{{
              useCoupon ? 0 : formatNumberWithCommas(getEngravePrice(equipInfo.supremeEngraveAttr?.count, equipInfo.supremeAttrs?.level))
            }}
            金币
          </p>
          <p v-if="engraveMaxQuality" style="justify-content: flex-start">
            刻印词缀最高品质：<span :class="engraveMaxQuality">{{ equipQualityText[engraveMaxQuality] }}</span>
          </p>
          <template v-if="equipInfo.supremeAttrs">
            <p style="justify-content: flex-start; align-items: center">
              <span>选择刻印词缀：</span>
              <el-radio-group v-model="engraveIndex" :disabled="Boolean(equipInfo.supremeEngraveAttr)">
                <el-radio v-for="(item, index) of equipInfo.supremeAttrs.affixList.slice(1)" :value="index + 1">
                  <span :class="supremeAffixLevelQualityMap[supremeAffixCollection[item].level]">{{ supremeAffixCollection[item].name }}</span>
                </el-radio>
              </el-radio-group>
            </p>
          </template>
          <!-- <template v-if="equipInfo.supremeEngraveAttr">
            <p style="display: block">
              刻印词条：
              <span class="affix" v-for="item of explainDarkGoldEquipAffix(equipInfo.supremeEngraveAttr.special, false)" v-html="item"></span>
            </p>
          </template> -->
        </div>
        <div class="btn-wrap">
          <el-button type="warning" plain @click="handleEngrave"> 开始刻印 </el-button>
        </div>
      </template>
    </div>
    <div class="item-list border-wrap" style="flex-shrink: 0; margin-bottom: 10px">
      <h4>已装备</h4>
      <div class="item-btn-wrap btn-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="(item, index) of equippedList" :key="index">
            <CommonBtn
              size="small"
              @done="handleChoice(item)"
              :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
              :type="item ? 'primary' : 'info'"
            >
              <span v-if="item" v-html="getEquipName(item, index)"></span>
              <span v-else>{{ equipIcon[equipTypeList[index]] }} (未装备)</span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
    </div>
    <div class="item-list border-wrap package">
      <h4>背包</h4>
      <p class="cur-gold">
        <span>💰 {{ formatNumberWithCommas(userStore.gold) }}</span>
      </p>
      <div class="item-btn-wrap btn-wrap item-list-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="item of itemList" :key="item.id">
            <CommonBtn size="small" @done="handleChoice(item)" :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }">
              <span v-html="getEquipName(item, item.type)"></span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
    </div>

    <el-dialog v-model="resShow" width="100%" style="max-width: 500px" :before-close="() => false">
      <h2 class="affix">
        <span class="grow">{{ isRecord ? '上次刻印结果未确认' : '刻印成功' }}</span>
      </h2>
      <p style="text-align: center" v-show="resEquipInfo">请选择保留的词缀</p>
      <div class="engrave-res">
        <div class="left border-wrap" v-if="equipInfo">
          <el-popover placement="bottom" width="300px" trigger="click" :teleported="false">
            <template #reference>
              <div>
                <CommonBtn size="small" style="width: 100%">
                  <span v-html="getEquipName(equipInfo, equipInfo.type)"></span>
                </CommonBtn>
              </div>
            </template>
            <EquipInfo :equip-info="equipInfo" :show-action="false" style="max-height: 50vh" />
          </el-popover>
          <template v-if="equipInfo.supremeAttrs?.affixList[engraveIndex]">
            <p :class="supremeAffixLevelQualityMap[supremeAffixCollection[equipInfo.supremeAttrs?.affixList[engraveIndex]].level]">
              {{ supremeAffixCollection[equipInfo.supremeAttrs?.affixList[engraveIndex]].name }}：
            </p>
            <p class="affix" v-for="(val, key) of supremeAffixCollection[equipInfo.supremeAttrs?.affixList[engraveIndex]].attrs">
              <template v-if="key !== 'skillGrow'">
                <span>{{ BasicAttrTextEnum[key] }}</span>
                <span v-if="val && (val as number) > 0">
                  +
                  <span class="grow">{{ val }}{{ noPercentEquipAttrKeyList.includes(key) ? '' : '%' }}</span>
                </span>
                <span v-else>
                  -
                  <span class="shrink">{{ Math.abs((val as number)!) }}{{ noPercentEquipAttrKeyList.includes(key) ? '' : '%' }}</span>
                </span>
              </template>
              <template v-else>
                <p v-for="(levelAdd, skillId) in val">
                  <span>{{ getSkillNameById(Number(skillId), true) }}</span>
                  <span
                    >+ <span class="grow">{{ levelAdd }}</span></span
                  >
                </p>
              </template>
            </p>
          </template>
          <div class="btn-wrap" v-show="resEquipInfo">
            <el-button type="primary" plain size="small" @click="handleChoiceEngraveRes(0)">保留</el-button>
          </div>
        </div>
        <div class="right border-wrap" v-if="resEquipInfo">
          <el-popover placement="bottom" width="300px" trigger="click" :teleported="false">
            <template #reference>
              <div>
                <CommonBtn size="small" style="width: 100%">
                  <span v-html="getEquipName(equipInfo, resEquipInfo.type)"></span>
                </CommonBtn>
              </div>
            </template>
            <EquipInfo :equip-info="resEquipInfo" :show-action="false" style="max-height: 50vh" />
          </el-popover>
          <template v-if="resEquipInfo.supremeEngraveAttr">
            <p :class="supremeAffixLevelQualityMap[supremeAffixCollection[resEquipInfo.supremeEngraveAttr.affixKey].level]">
              {{ supremeAffixCollection[resEquipInfo.supremeEngraveAttr.affixKey].name }}：
            </p>
            <p class="affix" v-for="(val, key) of supremeAffixCollection[resEquipInfo.supremeEngraveAttr.affixKey].attrs">
              <template v-if="key !== 'skillGrow'">
                <span>{{ BasicAttrTextEnum[key] }}</span>
                <span v-if="val && (val as number) > 0">
                  +
                  <span class="grow">{{ val }}{{ noPercentEquipAttrKeyList.includes(key) ? '' : '%' }}</span>
                </span>
                <span v-else>
                  -
                  <span class="shrink">{{ Math.abs((val as number)!) }}{{ noPercentEquipAttrKeyList.includes(key) ? '' : '%' }}</span>
                </span>
              </template>
              <template v-else>
                <p v-for="(levelAdd, skillId) in val">
                  <span>{{ getSkillNameById(Number(skillId), true) }}</span>
                  <span
                    >+ <span class="grow">{{ levelAdd }}</span></span
                  >
                </p>
              </template>
            </p>
          </template>
          <div class="btn-wrap">
            <el-button type="warning" plain size="small" @click="handleChoiceEngraveRes(1)">覆盖</el-button>
          </div>
        </div>
      </div>
      <template #footer>
        <p style="text-align: center" v-show="!resEquipInfo">
          <el-button plain @click="resShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.enchant {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  .enchant-table {
    position: relative;
    padding: 0 8px 10px 8px;
    margin-bottom: 10px;
    overflow: auto;
    .enchant-equip {
      border: 1px var(--fontColor) solid;
      padding: 6px 8px;
      border-radius: 4px;
      background-color: rgba($color: var(--fontColor), $alpha: 0.1);
    }
    .help-info {
      position: absolute;
      top: 5px;
      right: 10px;
    }
  }
  .item-list {
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    justify-content: unset;
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
    .cur-gold {
      width: 100%;
      text-align: right;
      font-size: 14px;
      padding-right: 10px;
      margin: 0;
    }
    .item-btn-wrap {
      overflow: auto;
    }
    &.package {
      flex: 1;
      min-height: 135px;
    }
  }
  .btn-wrap {
    & > div {
      justify-content: unset;
      margin-bottom: 0;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
  .engrave-res {
    display: flex;
    justify-content: space-between;
    > div {
      width: calc(50% - 5px);
      margin: 0 auto;
      flex: 1;
    }
    .border-wrap {
      padding: 10px;
      p {
        margin: 4px 0;
      }
    }
  }
}
</style>
